// src/components/configuration/sections/services/ServiceListItem.jsx
import React from 'react';
import {
  DollarSign,
  Clock,
  Package,
  Trash2
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../utils/cn';

const ServiceListItem = ({
  service,
  isSelected = false,
  onClick,
  onDelete
}) => {
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'Not set';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'Not set';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const componentCount = service.components?.length || 0;

  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative cursor-pointer transition-all duration-200 group p-3",
        "hover:shadow-md hover:scale-[1.01]",
        "rounded-lg border bg-transparent backdrop-blur-none shadow-none",
        isSelected
          ? "bg-accent border-primary shadow-md"
          : "bg-card border-border hover:border-muted-foreground"
      )}
    >
      {/* Selected indicator bar */}
      {isSelected && (
        <div
          className="absolute left-0 top-3 bottom-3 w-1 rounded-r"
          style={{ backgroundColor: service.color_code || '#8B5CF6' }}
        />
      )}

      {/* Header Section */}
      <CardHeader className="p-0 pb-2 space-y-0">
        <div className="flex items-center gap-1.5 w-full">
          {/* Color Indicator */}
          <Badge
            className={cn(
              "w-3 h-3 rounded-full border-2 transition-all p-0",
              isSelected ? "border-primary scale-110" : "border-border"
            )}
            style={{ backgroundColor: service.color_code || '#8B5CF6' }}
          />

          {/* Service Name */}
          <h3 className={cn(
            "font-semibold truncate transition-colors flex-1 min-w-0 text-sm",
            isSelected ? "text-foreground" : "text-foreground/90"
          )}>
            {service.name}
          </h3>

          {/* Active Status Dot */}
          <Badge
            className={cn(
              "w-2 h-2 rounded-full p-0 border-0",
              service.active ? "bg-emerald-500" : "bg-muted-foreground/50"
            )}
          />

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>

      {/* Content Section */}
      <CardContent className="p-0 space-y-1.5">
        {/* Price & Add-ons Row */}
        <div className="flex items-center gap-3 text-xs flex-wrap">
          {/* Price */}
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="text-muted-foreground font-medium whitespace-nowrap">
              {formatPrice(service.base_price)}
            </span>
          </div>

          {/* Components */}
          {componentCount > 0 && (
            <div className="flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-purple-500 shrink-0" />
              <span className={cn(
                "font-medium whitespace-nowrap",
                isSelected ? "text-primary" : "text-muted-foreground"
              )}>
                {componentCount} add-on{componentCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Duration */}
        <div className="flex items-center gap-1.5 text-xs">
          <Clock className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          <span className="text-muted-foreground font-medium whitespace-nowrap">
            {formatDuration(service.base_duration)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceListItem;
